import { useEffect, useMemo, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DialogSimple from '@/components/dialog/DialogSimple';
import {
  applyBookingRecurrenceChange,
  BookingRecurrenceChangePayload,
  BookingRecurrenceChangePreviewItem,
  BookingRecurrenceChangePreviewResponse,
  getFilteredBookingSession,
  previewBookingRecurrenceChange,
} from '@/features/bookings-management/api';
import { BookingSession } from '@/features/bookings-management/types';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

type RecurrenceDayChangeDialogProps = {
  bookingId: number;
  isDialogOpen: boolean;
  recurringDays: string;
  previousRecurringDays: string;
  intervalNumber?: 1 | 2;
  previousIntervalNumber?: number | null;
  onCloseDialog: VoidFunction;
  onApplied: VoidFunction;
  onSaveBookingDetails?: (
    effectivePayload?: Pick<
      BookingRecurrenceChangePayload,
      'effective_from_date' | 'effective_from_session_id'
    >
  ) => Promise<void>;
};

type PreviewSectionConfig = {
  label: string;
  color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
  keys: string[];
};

const PREVIEW_SECTIONS: PreviewSectionConfig[] = [
  {
    label: 'Moved',
    color: 'primary',
    keys: ['reused_sessions', 'moved', 'moved_sessions'],
  },
  { label: 'Created', color: 'success', keys: ['created', 'created_sessions'] },
  {
    label: 'Removed',
    color: 'warning',
    keys: ['removed_session_ids', 'deleted', 'deleted_sessions'],
  },
  {
    label: 'Unassigned',
    color: 'info',
    keys: ['unassigned', 'unassigned_sessions'],
  },
  {
    label: 'Cleaner changes',
    color: 'default',
    keys: ['cleaner_results', 'skipped', 'skipped_sessions'],
  },
  {
    label: 'Protected',
    color: 'error',
    keys: ['protected', 'protected_sessions'],
  },
  {
    label: 'Conflicts',
    color: 'error',
    keys: ['conflicts', 'conflict_sessions'],
  },
];

const toDateValue = (value?: string | null) => value?.slice(0, 10) ?? '';

const getSessionTimestamp = (session: BookingSession) =>
  `${toDateValue(session.date)}T${session.time ?? '00:00'}`;

const sortSessionsByDateTime = (sessions: BookingSession[]) =>
  [...sessions].sort((firstSession, secondSession) =>
    getSessionTimestamp(firstSession).localeCompare(
      getSessionTimestamp(secondSession)
    )
  );

const formatDisplayDate = (value?: string | null) => {
  if (!value) return '';
  const parsedDate = parseISO(toDateValue(value));

  return isValid(parsedDate) ? format(parsedDate, 'EEE, d MMM yyyy') : value;
};

const formatSessionLabel = (session: BookingSession) =>
  `#${session.id} - ${formatDisplayDate(session.date)}${
    session.time ? ` at ${session.time}` : ''
  }`;

const getPreviewSources = (
  response: BookingRecurrenceChangePreviewResponse | null
) => {
  if (!response) return [];

  const sources: Record<string, unknown>[] = [response];
  if (response.data && typeof response.data === 'object') {
    sources.push(response.data as Record<string, unknown>);
  }

  return sources;
};

const getPreviewItems = (
  response: BookingRecurrenceChangePreviewResponse | null,
  keys: string[]
): BookingRecurrenceChangePreviewItem[] =>
  getPreviewSources(response).flatMap((source) =>
    keys.flatMap((key) => {
      const value = source[key];
      if (!Array.isArray(value)) return [];

      return value.map((item) =>
        typeof item === 'number'
          ? ({ session_id: item } as BookingRecurrenceChangePreviewItem)
          : (item as BookingRecurrenceChangePreviewItem)
      );
    })
  );

const getPreviewFlag = (
  response: BookingRecurrenceChangePreviewResponse | null,
  key: 'blocked' | 'can_apply'
) => {
  const flag = getPreviewSources(response)
    .map((source) => source[key])
    .find((value) => typeof value === 'boolean');

  return typeof flag === 'boolean' ? flag : undefined;
};

const getPreviewMessage = (
  response: BookingRecurrenceChangePreviewResponse | null
) => {
  const message = getPreviewSources(response)
    .map((source) => source.message)
    .find((value) => typeof value === 'string');

  return typeof message === 'string' ? message : '';
};

const getPreviewItemId = (item: BookingRecurrenceChangePreviewItem) =>
  item.session_id ?? item.booking_session_id ?? item.id;

const getPreviewItemText = (item: BookingRecurrenceChangePreviewItem) => {
  const id = getPreviewItemId(item);
  const dateChange =
    item.old_date || item.new_date || item.from_date || item.to_date
      ? `${
          formatDisplayDate(String(item.old_date ?? item.from_date ?? '')) ||
          'N/A'
        } -> ${
          formatDisplayDate(String(item.new_date ?? item.to_date ?? '')) ||
          'N/A'
        }`
      : formatDisplayDate(String(item.date ?? ''));
  const cleaner = item.cleaner_name ? `Cleaner: ${item.cleaner_name}` : '';
  const cleanerId = item.cleaner_id ? `Cleaner #${item.cleaner_id}` : '';
  const action = item.action ? `Action: ${item.action}` : '';
  const reasons = Array.isArray(item.reasons) ? item.reasons.join(', ') : '';
  const message = item.message ?? item.reason ?? (reasons || item.status) ?? '';

  return [
    id ? `Session #${id}` : '',
    dateChange,
    item.time ? `Time: ${item.time}` : '',
    cleaner,
    cleanerId,
    action,
    message,
  ]
    .filter(Boolean)
    .join(' - ');
};

export default function RecurrenceDayChangeDialog({
  bookingId,
  isDialogOpen,
  recurringDays,
  previousRecurringDays,
  intervalNumber,
  previousIntervalNumber,
  onCloseDialog,
  onApplied,
  onSaveBookingDetails,
}: RecurrenceDayChangeDialogProps) {
  const queryClient = useQueryClient();
  const { successMsg, errorMsg } = useSnackbarMsg();
  const [reason, setReason] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState('');
  const [effectiveFromSessionId, setEffectiveFromSessionId] = useState('');
  const [previewResponse, setPreviewResponse] =
    useState<BookingRecurrenceChangePreviewResponse | null>(null);
  const [previewPayloadKey, setPreviewPayloadKey] = useState('');
  const [isSavingBookingDetails, setIsSavingBookingDetails] = useState(false);
  const [applyDespiteProtectedSessions, setApplyDespiteProtectedSessions] =
    useState(false);
  const todayDate = format(new Date(), 'yyyy-MM-dd');

  const { data: upcomingSessions, isLoading: isLoadingUpcomingSessions } =
    useQuery({
      queryKey: ['bookingSessions', bookingId, 'upcoming'],
      queryFn: () => getFilteredBookingSession(bookingId, 'upcoming'),
      enabled: isDialogOpen && Number.isFinite(bookingId),
      onError: (error) => {
        errorMsg(error, 'Unable to load upcoming sessions');
      },
      refetchOnWindowFocus: false,
    });

  const sortedUpcomingSessions = useMemo(
    () => sortSessionsByDateTime(upcomingSessions ?? []),
    [upcomingSessions]
  );

  useEffect(() => {
    if (!isDialogOpen) return;

    setReason('');
    setEffectiveFromDate('');
    setEffectiveFromSessionId('');
    setPreviewResponse(null);
    setPreviewPayloadKey('');
    setApplyDespiteProtectedSessions(false);
  }, [isDialogOpen, bookingId, recurringDays]);

  useEffect(() => {
    if (
      !isDialogOpen ||
      effectiveFromDate ||
      sortedUpcomingSessions.length === 0
    ) {
      return;
    }

    const firstUpcomingSession = sortedUpcomingSessions[0];
    setEffectiveFromSessionId(String(firstUpcomingSession.id));
    setEffectiveFromDate(toDateValue(firstUpcomingSession.date));
  }, [effectiveFromDate, isDialogOpen, sortedUpcomingSessions]);

  const selectedSessionId = Number(effectiveFromSessionId);
  const selectedSession = Number.isFinite(selectedSessionId)
    ? sortedUpcomingSessions.find((session) => session.id === selectedSessionId)
    : undefined;

  const payload: BookingRecurrenceChangePayload = {
    recurring_days: recurringDays,
    effective_from_date: effectiveFromDate,
    ...(selectedSession
      ? { effective_from_session_id: selectedSession.id }
      : {}),
    ...(intervalNumber ? { interval_number: intervalNumber } : {}),
    reason: reason.trim(),
    cleaner_policy: 'keep_if_available',
  };

  const currentPayloadKey = JSON.stringify(payload);
  const hasRequiredPayload =
    Boolean(payload.recurring_days) &&
    Boolean(payload.effective_from_date) &&
    Boolean(payload.reason) &&
    payload.effective_from_date >= todayDate;
  const effectiveFromDateIsPast =
    Boolean(effectiveFromDate) && effectiveFromDate < todayDate;

  const previewMutation = useMutation({
    mutationFn: () => previewBookingRecurrenceChange(bookingId, payload),
    onSuccess: (response) => {
      setPreviewResponse(response);
      setPreviewPayloadKey(currentPayloadKey);
      setApplyDespiteProtectedSessions(false);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to preview recurrence day changes');
    },
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      applyBookingRecurrenceChange(bookingId, {
        ...payload,
        ...(applyDespiteProtectedSessions
          ? { apply_despite_protected_sessions: true }
          : {}),
      }),
    onSuccess: () => {
      successMsg('Recurrence day changes applied');
      queryClient.invalidateQueries(['bookings']);
      queryClient.invalidateQueries(['bookingSessions']);
      onApplied();
    },
    onError: (error) => {
      errorMsg(error, 'Unable to apply recurrence day changes');
    },
  });

  const protectedItems = getPreviewItems(previewResponse, [
    'protected',
    'protected_sessions',
  ]);
  const conflictItems = getPreviewItems(previewResponse, [
    'conflicts',
    'conflict_sessions',
  ]);
  const previewBlockedByFlag =
    getPreviewFlag(previewResponse, 'blocked') === true ||
    getPreviewFlag(previewResponse, 'can_apply') === false;
  const hasProtectedItems = protectedItems.length > 0;
  const hasConflictItems = conflictItems.length > 0;
  const protectedBlockIsOverridden =
    hasProtectedItems && applyDespiteProtectedSessions;
  const hasHardBlock =
    hasConflictItems || (previewBlockedByFlag && !hasProtectedItems);
  const previewBlocked =
    hasHardBlock || (hasProtectedItems && !protectedBlockIsOverridden);
  const previewIsCurrent =
    !!previewResponse && previewPayloadKey === currentPayloadKey;
  const canApplyPreview =
    hasRequiredPayload &&
    previewIsCurrent &&
    !previewBlocked &&
    !applyMutation.isLoading &&
    !isSavingBookingDetails;

  const handleChangeReason = (value: string) => {
    setReason(value);
    setPreviewResponse(null);
    setPreviewPayloadKey('');
    setApplyDespiteProtectedSessions(false);
  };

  const handleChangeEffectiveDate = (value: string) => {
    setEffectiveFromDate(value);
    setEffectiveFromSessionId('');
    setPreviewResponse(null);
    setPreviewPayloadKey('');
    setApplyDespiteProtectedSessions(false);
  };

  const handleChangeSession = (value: string) => {
    setEffectiveFromSessionId(value);
    setPreviewResponse(null);
    setPreviewPayloadKey('');
    setApplyDespiteProtectedSessions(false);

    const nextSessionId = Number(value);
    const nextSession = sortedUpcomingSessions.find(
      (session) => session.id === nextSessionId
    );
    if (nextSession) {
      setEffectiveFromDate(toDateValue(nextSession.date));
    }
  };

  const handlePreview = () => {
    if (!hasRequiredPayload) {
      errorMsg(
        effectiveFromDateIsPast
          ? 'Choose today or a future date before previewing changes'
          : 'Choose a cutover date and enter a reason before previewing changes'
      );
      return;
    }

    previewMutation.mutate();
  };

  const handleApply = async () => {
    if (!canApplyPreview) return;

    try {
      setIsSavingBookingDetails(true);
      await onSaveBookingDetails?.({
        effective_from_date: payload.effective_from_date,
        ...(payload.effective_from_session_id
          ? { effective_from_session_id: payload.effective_from_session_id }
          : {}),
      });
    } catch (error) {
      errorMsg(
        error,
        'Unable to save booking details before recurrence change'
      );
      return;
    } finally {
      setIsSavingBookingDetails(false);
    }

    applyMutation.mutate();
  };

  const handleClose = () => {
    if (
      previewMutation.isLoading ||
      applyMutation.isLoading ||
      isSavingBookingDetails
    ) {
      return;
    }

    onCloseDialog();
  };

  const previewMessage = getPreviewMessage(previewResponse);
  const previewHasRows = PREVIEW_SECTIONS.some(
    (section) => getPreviewItems(previewResponse, section.keys).length > 0
  );

  return (
    <DialogSimple
      dialogTitle="Preview recurrence day changes"
      open={isDialogOpen}
      loadingButton={false}
      disagreeButton={false}
      maxWidth="md"
      onClickButtonClose={handleClose}
      dialogCloseOutside={handleClose}
    >
      <Stack spacing={2.5} sx={{ px: 4, pb: 3 }}>
        <Alert severity="info">
          Existing sessions before the cutover date stay unchanged. Future
          sessions from the cutover will be regenerated after preview.
        </Alert>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Current days"
            value={previousRecurringDays || 'N/A'}
            size="small"
            fullWidth
            disabled
          />
          <TextField
            label="New days"
            value={recurringDays || 'N/A'}
            size="small"
            fullWidth
            disabled
          />
        </Stack>

        {intervalNumber && previousIntervalNumber && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Current frequency"
              value={previousIntervalNumber === 2 ? 'Fortnightly' : 'Weekly'}
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="New frequency"
              value={intervalNumber === 2 ? 'Fortnightly' : 'Weekly'}
              size="small"
              fullWidth
              disabled
            />
          </Stack>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="First affected session"
            size="small"
            fullWidth
            value={effectiveFromSessionId}
            onChange={(event) => handleChangeSession(event.target.value)}
            disabled={isLoadingUpcomingSessions}
            helperText={
              sortedUpcomingSessions.length === 0
                ? 'No upcoming sessions were returned'
                : undefined
            }
          >
            <MenuItem value="">Custom date</MenuItem>
            {sortedUpcomingSessions.map((session) => (
              <MenuItem key={session.id} value={String(session.id)}>
                {formatSessionLabel(session)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Effective from date"
            type="date"
            size="small"
            fullWidth
            value={effectiveFromDate}
            onChange={(event) => handleChangeEffectiveDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayDate }}
            error={effectiveFromDateIsPast}
            helperText={
              effectiveFromDateIsPast
                ? 'Choose today or a future date'
                : undefined
            }
          />
        </Stack>

        <TextField
          label="Reason for change"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={reason}
          onChange={(event) => handleChangeReason(event.target.value)}
        />

        {previewResponse && (
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography fontWeight={600}>Preview</Typography>
              {previewBlocked && (
                <Chip label="Blocked" color="error" size="small" />
              )}
              {protectedBlockIsOverridden && (
                <Chip
                  label="Protected override"
                  color="warning"
                  size="small"
                />
              )}
            </Stack>
            {previewMessage && (
              <Alert
                severity={previewBlocked ? 'warning' : 'success'}
                sx={{ mb: 1.5 }}
              >
                {previewMessage}
              </Alert>
            )}
            {!previewIsCurrent && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Preview is stale. Preview again before applying.
              </Alert>
            )}
            {!previewHasRows && (
              <Typography variant="body2" color="text.secondary">
                No affected sessions were returned by the preview.
              </Typography>
            )}
            {hasProtectedItems && previewIsCurrent && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applyDespiteProtectedSessions}
                      onChange={(event) =>
                        setApplyDespiteProtectedSessions(event.target.checked)
                      }
                    />
                  }
                  label="Apply despite protected sessions"
                />
              </Alert>
            )}
            {PREVIEW_SECTIONS.map((section) => {
              const items = getPreviewItems(previewResponse, section.keys);
              if (items.length === 0) return null;

              return (
                <Box key={section.label} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${section.label}: ${items.length}`}
                      color={section.color}
                      size="small"
                    />
                  </Stack>
                  <List dense disablePadding sx={{ mt: 0.5 }}>
                    {items.slice(0, 8).map((item, index) => (
                      <ListItem
                        key={`${section.label}-${index}`}
                        disableGutters
                      >
                        <ListItemText
                          primary={getPreviewItemText(item) || section.label}
                        />
                      </ListItem>
                    ))}
                    {items.length > 8 && (
                      <ListItem disableGutters>
                        <ListItemText
                          primary={`+${items.length - 8} more`}
                          primaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </List>
                  <Divider />
                </Box>
              );
            })}
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button color="inherit" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="outlined"
            onClick={handlePreview}
            loading={previewMutation.isLoading}
            disabled={!hasRequiredPayload || applyMutation.isLoading}
          >
            Preview changes
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleApply}
            loading={applyMutation.isLoading || isSavingBookingDetails}
            disabled={!canApplyPreview}
          >
            Apply changes
          </LoadingButton>
        </Stack>
      </Stack>
    </DialogSimple>
  );
}
